import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import AbacusClientUtils from '@homeaway/abacus-client-utils';
import determineIfMobile from '@homeaway/react-determine-if-mobile';

import {ListYourProperty} from './list-your-property/ListYourProperty';
import {Logo} from './logo/Logo';
import {Nav} from './nav/Nav';
import {Tray} from './tray/Tray';
import Feedback from '@homeaway/react-feedback';
import {EdapProvider} from '@homeaway/react-event-tracking';


import './SiteHeader.less';
import NotificationBar from './notification-bar/NotificationBar';

// TODO: consider how best to integrate A/B test state
// let multivtest = require('@homeaway/stab-multivtest-utils');

class SiteHeader extends React.Component {
    static propTypes = {
        bgWhite: PropTypes.bool,
        currencyCode: PropTypes.string,
        displayNotificationBar: PropTypes.bool,
        displayTrayJewel: PropTypes.bool,
        fullLocale: PropTypes.string,
        homeAwayWhiteLogoImage: PropTypes.string,
        inverseVrboLogoImage: PropTypes.string,
        isFlagsDisabled: PropTypes.bool,
        isInverse: PropTypes.bool,
        isTrayOpen: PropTypes.bool,
        logoutRedirectUrl: PropTypes.string,
        /**
         * Overrides default implementation of Global selector change handler. See docs for example of usage.
         */
        onGlobalSelectorChange: PropTypes.func,
        onTrayChange: PropTypes.func,
        onNotificationBarDismiss: PropTypes.func,
        onNotificationMessageDismiss: PropTypes.func,
        notificationMessages: PropTypes.arrayOf(PropTypes.node),
        theme: PropTypes.string,
        tripboard: PropTypes.oneOfType([PropTypes.node, PropTypes.element, PropTypes.func]),
        vrboLogoImage: PropTypes.string,
        withoutCountryLanguageSelector: PropTypes.bool,
        withoutCurrencySelector: PropTypes.bool,
        withoutFlags: PropTypes.bool,
        withoutHelpLinks: PropTypes.bool,
        withoutInbox: PropTypes.bool,
        withoutListYourProperty: PropTypes.bool,
        withoutLogin: PropTypes.bool,
        withoutNav: PropTypes.bool,
        withoutScratchpad: PropTypes.bool,
        withoutSearch: PropTypes.bool,
        withoutTraveler: PropTypes.bool,
        withoutTray: PropTypes.bool,
        withoutAccessibility: PropTypes.bool
    };

    static defaultProps = {
        defaultAvatar: '//csvcus.homeaway.com/rsrcs/cdn-logos/3.0.0/bce/brand/misc/default-profile-pic.svg',
        displayNotificationBar: false,
        displayTrayJewel: false,
        features: {},
        fullLocale: 'en_US_HA',
        homeAwayWhiteLogoImage: '//csvcus.homeaway.com/rsrcs/cdn-logos/3.0.0/bce/moniker/homeaway_us/logo-bceheader-white.svg',
        isInverse: false,
        notificationMessages: [],
        onNotificationBarDismiss: () => { },
        onNotificationMessageDismiss: () => { },
        onTrayChange: () => { },
        withoutCountryLanguageSelector: false,
        withoutCurrencySelector: false,
        vrboLogoImage: '//csvcus.homeaway.com/rsrcs/cdn-logos/3.0.0/bce/moniker/vrbo/logo.svg'
    };

    constructor(props) {
        super(props);

        const hasSearch = !!props.features && !!props.features.search;

        this.state = {
            hasSearch,
            isTrayOpen: props.isTrayOpen
        };
    }

    /* istanbul ignore next */
    componentDidMount() {
        // @ts-ignore
        if (window && window.__ABACUS__) {
            let experimentNames = ['HA_GX_COUNTRY_LANGUAGE_SELECTOR', 'HA_GX_COUNTRY_LANGUAGE_SELECTOR_NL', 'HA_GX_CURRENCY_SELECTOR', 'HA_PTA_AFFILIATE_CO_BRAND'];

            for (let experimentName of experimentNames) {
                // @ts-ignore
                if (window.__ABACUS__[experimentName]) {
                    AbacusClientUtils.evaluateExperimentAndLog(experimentName);
                }
            }
        }
    }

    componentWillReceiveProps(props) {
        if (this.props.isTrayOpen !== props.isTrayOpen) {
            this.setState({
                isTrayOpen: props.isTrayOpen,
            });
        }
    }

    get className() {
        return classNames(
            'site-header',
            this.props.isInverse && 'site-header--inverse',
            'border-border-color',
            this.props.theme || this.props.siteName,
            this.props.bgWhite && 'site-header--white'
        );
    }

    get accessibility() {
        if (this.props.withoutAccessibility) {
            return false;
        }
        return this.props.accessibility;
    }

    get countryCode() {
        return this.props.fullLocale && this.props.fullLocale.split('_')[1].toLowerCase();
    }

    get currencyCode() {
        return this.props.currencyCode;
    }

    get currencySelector() {
        return this.props.currencySelector;
    }

    get flags() {
        if (this.props.withoutFlags) {
            return false;
        }

        return this.props.flags;
    }

    get help() {
        if (this.props.withoutHelpLinks && this.props.help) {
            let help = {...this.props.help};

            help.items = help.items.slice(0, 6);

            return help;
        }

        return this.props.help;
    }

    get languageCode() {
        return this.props.fullLocale.substr(0, 5);
    }

    get login() {
        if (this.props.withoutLogin) {
            return false;
        }

        if (this.props.login && this.props.travelerLoginClickHandler) {
            this.props.login.travelerLoginClickHandler = this.props.travelerLoginClickHandler;
        }

        return this.props.login;
    }

    get scratchpad() {
        if (this.props.withoutScratchpad) {
            return false;
        }

        return this.props.scratchpad;
    }

    get traveler() {
        if (this.props.withoutTraveler) {
            return false;
        }

        // defensive copy to protect props as updateLogoutUrl and determineAvatar are destructive
        const traveler = typeof (this.props.traveler) !== 'undefined' ? JSON.parse(JSON.stringify(this.props.traveler)) : this.props.traveler;

        this.updateLogoutUrl(traveler);

        return this.determineAvatar(traveler);
    }

    get travelerInbox() {
        if (this.props.withoutInbox) {
            return false;
        }

        return this.props.travelerInbox;
    }

    get tripboard() {
        return this.props.tripboard;
    }

    determineAvatar = (traveler) => {
        if (traveler && traveler.avatar && !traveler.avatar.image) {
            traveler.avatar.image = this.props.defaultAvatar;
        }

        return traveler;
    }

    updateLogoutUrl = (traveler) => {
        if (!traveler || !traveler.items) {
            return;
        }

        let itemIndex;
        let item;

        traveler.items.forEach((currentItem, i) => {
            if (currentItem.cssClass === 'se-traveler-logout') {
                itemIndex = i;
                item = currentItem;
            }
        });

        if (!this.props.logoutRedirectUrl || !item) {
            return;
        }

        const serviceRE = /(^.*service=)(.*)/;

        // update traveler's logout link to use logoutUrl prop instead
        const newServiceParameter = encodeURIComponent(this.props.logoutRedirectUrl);
        item.link = item.link.replace(serviceRE, `$1${newServiceParameter}`);
        item.linkUpdated = true;
        traveler.items[itemIndex] = item;
    }

    handleToggleOpenTray = () => {
        let body = document.querySelector('body');

        this.setState({
            isTrayOpen: !this.state.isTrayOpen,
        }, () => {
            // FIXME: Not ideal, but this gives us the desired in/out animation
            /* istanbul ignore next */
            if (this.state.isTrayOpen) {
                body.classList.add('site-header-tray-open', 'site-header-tray-fixed');
            } else {
                body.classList.remove('site-header-tray-open');
                setTimeout(() => body.classList.remove('site-header-tray-fixed'), 500);
            }
            this.props.onTrayChange(this.state.isTrayOpen);
        });
    }

    // Allow passing in custom classnames so that
    // feedback-component is more flexible when rendering
    // within contexts outside of its default use case.
    feedbackComponent = ({className = '', feedbackTabClassList = ''}) => <Feedback className={className} feedbackTabClassList={feedbackTabClassList} lingua={this.props.feedbackComponentLingua} />;

    // eslint-disable-next-line complexity
    render() {
        const {
            feedbackComponentLingua,
            displayTrayJewel,
            displayNotificationBar,
            onGlobalSelectorChange
        } = this.props;

        let siteHeaderTrayToggleClass = 'site-header__tray-toggle hidden-lg';
        let siteHeaderTrayToggleAriaLabel = 'Open site navigation';
        if (displayTrayJewel) {
            siteHeaderTrayToggleClass += ' display-jewel';
        }
        if (this.state.isTrayOpen) {
            siteHeaderTrayToggleClass += ' site-header__tray-toggle--close';
            siteHeaderTrayToggleAriaLabel = 'Close site navigation';
        }

        return (

            <div className="site-header--wrapper">

                <NotificationBar
                    show={displayNotificationBar}
                    onDismissMessage={this.props.handleNotificationMessageDismiss}
                    onDismissBar={this.props.handleNotificationBarDismiss}
                    messages={this.props.notificationMessages}
                />
                <div className={this.className}>
                    <EdapProvider>
                        {!this.props.withoutTray && (
                            <Tray
                                countryCode={this.countryCode}
                                currencyCode={this.currencyCode}
                                currencySelector={this.currencySelector}
                                extraNav={this.props.extraNav}
                                feedbackComponent={feedbackComponentLingua && this.feedbackComponent({className: 'site-header-tray__menu-item-link'})} // adding classnames of surrounding elements to feedback-component so it can inherit and fit in new context
                                flags={this.flags}
                                globalizationSelector={this.props.globalizationSelector}
                                handleToggleOpen={this.handleToggleOpenTray}
                                help={this.help}
                                inbox={this.travelerInbox}
                                inverse={this.props.isInverse}
                                isOpen={this.state.isTrayOpen}
                                isMobile={this.props.isMobile}
                                languageCode={this.languageCode}
                                login={this.login}
                                lyp={!this.props.withoutListYourProperty && this.props.lyp}
                                onGlobalSelectorChange={this.props.onGlobalSelectorChange}
                                renderCountryLanguageSelector={!this.props.withoutCountryLanguageSelector}
                                renderCurrencySelector={!this.props.withoutCurrencySelector}
                                scratchpad={this.scratchpad}
                                tripboard={this.tripboard}
                                traveler={this.traveler}
                            />
                        )}

                        {this.props.logo && (
                            <Logo
                                {...this.props.logo}
                                hasSearch={this.state.hasSearch}
                                image={this.props.logo.image}
                                partnerLogo={this.props.partnerLogo}
                                sitePartnerLogo={this.props.sitePartnerLogo}
                                link={this.props.logo.link}
                            />
                        )}

                        {/* Render children items to support mixins like search experience */}
                        <div className="site-header__flex-spacer">
                            {this.props.children}
                        </div>

                        {!this.props.withoutNav && (
                            <Nav
                                accessibility={this.accessibility}
                                countryCode={this.countryCode}
                                currencyCode={this.currencyCode}
                                currencySelector={this.currencySelector}
                                extraNav={this.props.extraNav}
                                feedbackComponent={feedbackComponentLingua && this.feedbackComponent({className: 'site-header-nav__link site-header-nav__dropdown'})} // adding classnames of surrounding elements to feedback-component so it can inherit and fit in new context
                                flags={this.flags}
                                globalizationSelector={this.props.globalizationSelector}
                                help={this.help}
                                inbox={this.travelerInbox}
                                internalTools={this.props.internalTools}
                                isInverse={this.props.isInverse}
                                isMobile={this.props.isMobile}
                                languageCode={this.languageCode}
                                login={this.login}
                                onGlobalSelectorChange={onGlobalSelectorChange}
                                renderCountryLanguageSelector={!this.props.withoutCountryLanguageSelector}
                                renderCurrencySelector={!this.props.withoutCurrencySelector}
                                scratchpad={this.scratchpad}
                                tripboard={this.tripboard}
                                traveler={this.traveler}
                            />
                        )}

                        {!this.props.withoutTray && (
                            // eslint-disable-next-line jsx-a11y/anchor-is-valid
                            <a className={siteHeaderTrayToggleClass}
                                onClick={this.handleToggleOpenTray}
                                onKeyPress={this.handleToggleOpenTray}
                                role="button"
                                tabIndex={0}
                                aria-label={siteHeaderTrayToggleAriaLabel}
                            >
                                <span>
                                    <svg height="14" viewBox="0 0 18 14" width="18">
                                        <g className="rect-svg rect-svg--top" fillRule="nonzero">
                                            <rect className="rect-top" height="2" rx="1" width="18" />
                                        </g>
                                        <g className="rect-svg rect-svg--middle" fillRule="nonzero">
                                            <rect className="rect-middle" height="2" rx="1" width="18" y="6" />
                                        </g>
                                        <g className="rect-svg rect-svg--bottom" fillRule="nonzero">
                                            <rect className="rect-bottom" height="2" rx="1" width="18" y="12" />
                                        </g>
                                    </svg>
                                </span>
                            </a>
                        )}

                        {this.props.lyp && !this.props.withoutListYourProperty && (
                            <ListYourProperty
                                {...this.props.lyp}
                                isInverse={this.props.isInverse}
                            />
                        )}
                    </EdapProvider>
                </div>
            </div>

        );
    }
}

export default determineIfMobile(SiteHeader, 1199);
